import { useState } from "react";
import { Paperclip } from "lucide-react";
import { formatEUR } from "@/lib/format";
import { DemoFrame, Segmented } from "./DemoFrame";
import {
  DEMO_CLIENTS,
  DEMO_RIVAGE_INVOICES,
  DEMO_SENDER,
  DEMO_TEMPLATES,
  fillTemplate,
} from "./demoData";

const CLIENT = DEMO_CLIENTS[0]; // Atelier du Rivage

const CHOICES = [
  { value: "amiable", label: "Amiable" },
  { value: "deuxieme", label: "Deuxième relance" },
  { value: "ferme", label: "Ferme" },
  { value: "echeancier", label: "Échéancier" },
] as const;
type Choice = (typeof CHOICES)[number]["value"];

/**
 * Aperçu d'une relance, avec les modèles fournis par l'app : expéditeur
 * (votre Gmail), destinataire, objet, texte, tableau des factures,
 * signature et PDF joints. Rien n'est envoyé.
 */
export function ComposeDemo() {
  const [choice, setChoice] = useState<Choice>("amiable");
  const template =
    DEMO_TEMPLATES.find((t) => t.id === choice) ?? DEMO_TEMPLATES[0];
  const mail = fillTemplate(template, {
    client: CLIENT.name,
    amount: CLIENT.amount,
    days: CLIENT.days,
  });

  return (
    <DemoFrame
      title="Nouvelle relance"
      aside={
        <Segmented
          label="Modèle de relance"
          options={CHOICES}
          value={choice}
          onChange={setChoice}
        />
      }
      footnote="Aperçu : rien n’est envoyé depuis ce site. Dans l’app, vous relisez, puis vous cliquez sur Envoyer."
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 border-b border-border pb-3 text-ui">
        <dt className="text-subtle-foreground">De</dt>
        <dd className="min-w-0 truncate text-foreground">
          {DEMO_SENDER.email}{" "}
          <span className="ml-1 rounded-full bg-action-soft px-1.5 py-0.5 text-2xs font-semibold text-action-strong">
            votre Gmail
          </span>
        </dd>
        <dt className="text-subtle-foreground">À</dt>
        <dd className="truncate text-foreground">{CLIENT.contact}</dd>
        <dt className="text-subtle-foreground">Objet</dt>
        <dd data-testid="subject" className="font-semibold text-foreground">
          {mail.subject}
        </dd>
      </dl>

      <div
        key={choice}
        className="mt-4 space-y-2.5 text-sm leading-relaxed text-foreground"
        style={{ animation: "enc-rise 360ms var(--ease-quint-out) both" }}
      >
        {mail.paragraphs.map((p) => (
          <p key={p}>{p}</p>
        ))}
        {/* Comme l'email réel : le tableau suit la formule de politesse. */}
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="my-3 w-full min-w-[340px] text-ui">
            <thead>
              <tr className="border-b border-border text-left text-2xs tracking-wide text-subtle-foreground uppercase">
                <th className="py-1.5 font-semibold">N°</th>
                <th className="py-1.5 font-semibold">Émission</th>
                <th className="py-1.5 font-semibold">Échéance</th>
                <th className="py-1.5 text-right font-semibold">Reste à payer</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_RIVAGE_INVOICES.map((inv) => (
                <tr key={inv.number} className="border-b border-border">
                  <td className="py-1.5 font-mono text-caption">{inv.number}</td>
                  <td className="py-1.5">{inv.issued}</td>
                  <td className="py-1.5">{inv.due}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">{formatEUR(inv.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td colSpan={3} className="py-1.5 text-right text-2xs tracking-wide uppercase">
                  Total
                </td>
                <td className="py-1.5 text-right whitespace-nowrap">{formatEUR(CLIENT.amount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="font-display text-base italic text-foreground">
          {DEMO_SENDER.person}
          <span className="not-italic text-muted-foreground">
            {" "}
            · {DEMO_SENDER.company}
          </span>
        </p>
      </div>

      <ul
        aria-label="Pièces jointes"
        className="mt-4 flex flex-wrap gap-1.5 border-t border-border pt-3"
      >
        {DEMO_RIVAGE_INVOICES.map((inv) => (
          <li
            key={inv.number}
            className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 font-mono text-caption text-muted-foreground"
          >
            <Paperclip aria-hidden className="size-3.5 text-action" />
            {inv.number}.pdf
          </li>
        ))}
      </ul>
    </DemoFrame>
  );
}
